import { useCallback, useEffect, useRef, useState } from 'react';
import type { Tab, TabType } from '../../types/tab';
import TabItem from './TabItem';

interface Props {
    tabs: Tab[];
    activeTabKey: string | null;
    onSelect: (tabType: TabType, tabKey: string) => void;
    onClose: (tabKey: string) => void;
}

const Tabs = ({ tabs, activeTabKey, onSelect, onClose }: Props) => {
    const wrapperRef = useRef<HTMLDivElement>(null);

    const [showCloseButtons, setShowCloseButtons] = useState(false);
    const [showTitles, setShowTitles] = useState(false);

    const recalculateHidingCloseButtons = useCallback(() => {
        const wrapperWidth = wrapperRef.current?.clientWidth;

        if (wrapperWidth) {
            const elements = document.getElementsByClassName('tab-header-item');

            // if wrapper width can't fit the elements x100px
            // then hide the close button for tabs other than the active
            setShowCloseButtons(elements.length * 100 < wrapperWidth);

            // if wrapper width can't fit the elements x75px
            // then hide the title and icons for all tabs
            setShowTitles(elements.length * 75 < wrapperWidth);
        }
    }, []);

    useEffect(() => {
        const onResize = () => recalculateHidingCloseButtons();
        window.addEventListener('resize', onResize);

        return () => window.removeEventListener('resize', onResize);
    }, [recalculateHidingCloseButtons]);

    /**
     * biome-ignore lint/correctness/useExhaustiveDependencies:
     * We need to recalculate the hiding of close buttons whenever tabs change.
     * */
    useEffect(() => {
        recalculateHidingCloseButtons();
    }, [tabs, recalculateHidingCloseButtons]);

    return (
        <div ref={wrapperRef} className="tab-header-items-wrapper">
            <div className="tab-header-items">
                {tabs?.map((tab) => (
                    <TabItem
                        key={tab.key}
                        isActive={tab.key === activeTabKey}
                        tabType={tab.type}
                        tabKey={tab.key}
                        elementId={tab.elementId}
                        onSelect={onSelect}
                        onClose={onClose}
                        showCloseButton={showCloseButtons}
                        title={tab.title}
                        showTitle={showTitles}
                    />
                ))}
            </div>
        </div>
    );
};

export default Tabs;
